import { api } from '@goaltree/backend/convex/_generated/api'
import { habitLevel } from '@goaltree/backend/convex/lib/progression'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { Sprout, TreeDeciduous, TreePine } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function levelIcon(level: number) {
  return level <= 3 ? Sprout : level <= 6 ? TreeDeciduous : TreePine
}

// One shared subscription for every habit row on screen.
export function useHabitTotal(habitId: string) {
  const levels = useQuery(api.progression.getMyHabitLevels)
  return levels?.find((l) => l.habitId === habitId)?.total
}

// "Sapling · Lv 4" with a hairline bar toward the next stage.
export function HabitLevelChip({ habitId, className }: { habitId: string; className?: string }) {
  const { t } = useTranslation()
  const total = useHabitTotal(habitId)
  if (total === undefined) return null
  const lvl = habitLevel(total)
  const name = t(`progression.level.${lvl.name}`)
  const Icon = levelIcon(lvl.level)
  const title =
    lvl.next === null
      ? t('progression.levelMax')
      : t('progression.levelNext', {
          count: lvl.next - total,
          name: t(`progression.level.${habitLevel(lvl.next).name}`),
        })
  return (
    <span
      title={title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap font-mono text-[11px] leading-none text-muted-foreground',
        className,
      )}
    >
      <Icon className="h-2.5 w-2.5 shrink-0" strokeWidth={2.5} />
      {t('progression.levelChip', { name, level: lvl.level })}
      {lvl.next !== null && (
        <span className="h-[3px] w-5 overflow-hidden rounded-full bg-muted">
          <span
            className="block h-full rounded-full bg-primary/70"
            style={{ width: `${Math.max(8, lvl.progress * 100)}%` }}
          />
        </span>
      )}
    </span>
  )
}
